import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Zap } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (ready && user) nav("/", { replace: true }); }, [ready, user, nav]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0F1B3D] text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-500 flex items-center justify-center font-head font-extrabold text-xl">N</div>
            <div>
              <div className="font-head font-extrabold text-2xl leading-none">Nextra CircuitPay</div>
              <div className="text-blue-300 text-xs uppercase tracking-widest mt-1">Broadband &amp; ILL Payment Control</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <Zap className="text-blue-400 mb-4" size={40} />
          <h1 className="font-head text-4xl font-extrabold leading-tight">No active connection should go dark because a recharge was forgotten.</h1>
          <p className="text-slate-300 mt-4">Every morning, know exactly which broadband circuits must be recharged before the ISP disconnects them.</p>
        </div>
        <div className="relative z-10 text-xs text-slate-400 flex items-center gap-2"><ShieldCheck size={14} /> Role-based access · Full audit trail</div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute top-10 -right-20 w-72 h-72 rounded-full bg-blue-400/10 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-[var(--bg)]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <div className="font-head font-extrabold text-2xl">Nextra CircuitPay</div>
            <div className="text-slate-500 text-sm">Broadband &amp; ILL Payment Control</div>
          </div>
          <h2 className="font-head text-2xl font-bold">Sign in</h2>
          <p className="text-slate-500 text-sm mt-1">Access your renewal control dashboard.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password</label>
              <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
                className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-blue-500 outline-none" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit" disabled={loading}
              className="w-full h-11 rounded-lg bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white font-semibold transition-colors disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
